import React, { useState } from 'react';

import { Radio } from '@material-ui/core';

import { UiButton } from '../../../ui/ui-button';

import { ApplicantAutomationMessage } from './applicant-automation-message';
import { PersonalizedByTeamEngineRadio } from './personalized-by-team-engine-radio';
import { ViewDefaultDialog } from './view-default-dialog';

import { APPLICANT_AUTOMATION_COMMUNICATION_TYPES } from '../../utils/applicant-automation-communication-types';
import { getFollowUpDetails } from '../../utils/applicant-automation-display-utils';
import { isApplicantAutomationJobOverride } from '../../utils/is-applicant-automation';

type InputProps = {
  automationToRender: ApplicantAutomation;
  toggleEdit: () => void;
  handleRevert: () => void;
  turnUseApplicantQuestionsOn: () => void;
  turnUseApplicantQuestionsOff: () => void;
  orgName: string;
  canEditAutomation: boolean;
  jobOverrides: Array<ApplicantAutomation>;
  toggleShowJobOverridesDialog: () => void;
  applicationQuestionsToggleDisabled: boolean;
};

export const ApplicantAutomationComponent = ({
  automationToRender,
  toggleEdit,
  handleRevert,
  turnUseApplicantQuestionsOn,
  turnUseApplicantQuestionsOff,
  orgName,
  canEditAutomation,
  jobOverrides,
  toggleShowJobOverridesDialog,
  applicationQuestionsToggleDisabled,
}: InputProps) => {
  const [viewDefault, setViewDefault] = useState<boolean>(false);

  const toggleViewDefault = () => setViewDefault(!viewDefault);

  const allowApplicationQuestions =
    automationToRender.communicationType ===
      APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION &&
    !automationToRender.isFollowUp;

  const hasJobOverrides = jobOverrides && jobOverrides.length;

  return (
    <div className="u-margin-top">
      {!!automationToRender.isOnlySendDuringBusinessHours && (
        <div className="typo-subtitle">This message will only send during business hours.</div>
      )}
      {!!automationToRender.isFollowUp && (
        <div className="typo-subtitle">{getFollowUpDetails(automationToRender)}</div>
      )}
      {allowApplicationQuestions && (
        <PersonalizedByTeamEngineRadio
          checked={!!automationToRender.isUsingApplicationQuestions}
          togglePersonalize={turnUseApplicantQuestionsOn}
          disabled={applicationQuestionsToggleDisabled}
        />
      )}
      <div className="u-flex-center u-width-100">
        {allowApplicationQuestions && (
          <Radio
            color="primary"
            onClick={turnUseApplicantQuestionsOff}
            checked={!automationToRender.isUsingApplicationQuestions}
            disabled={applicationQuestionsToggleDisabled}
          />
        )}
        <div className="u-width-100">
          <ApplicantAutomationMessage
            automation={automationToRender}
            orgName={orgName}
            hideStaticPersonalizedMessage
          />
          {hasJobOverrides ? (
            <div className="u-margin-left-md">
              <span
                role="presentation"
                onClick={toggleShowJobOverridesDialog}
                className="typo-link"
              >
                {jobOverrides.length} Job{jobOverrides.length === 1 ? '' : 's'}
              </span>{' '}
              {jobOverrides.length === 1 ? 'has a custom message' : 'have custom messages'}
            </div>
          ) : null}
        </div>
      </div>
      <div
        className={`${
          isApplicantAutomationJobOverride(automationToRender)
            ? 'u-justifyContent-spaceBetween'
            : 'u-justifyContent-flexEnd'
        } u-display-flex u-width-100 u-margin-top`}
      >
        {isApplicantAutomationJobOverride(automationToRender) ? (
          <div className="u-display-flex">
            <UiButton onClick={toggleViewDefault} variant="tertiary" size="xs">
              View Default
            </UiButton>
            <UiButton
              onClick={handleRevert}
              className="u-margin-left-md"
              variant="tertiary"
              size="xs"
            >
              Revert to Default
            </UiButton>
          </div>
        ) : null}
        {canEditAutomation &&
          !(allowApplicationQuestions && automationToRender.isUsingApplicationQuestions) && (
            <UiButton variant="tertiary" onClick={toggleEdit} size="xs">
              Edit
            </UiButton>
          )}
      </div>
      {viewDefault && automationToRender.overrideForCommunicationId && (
        <ViewDefaultDialog
          overriddenAutomationId={automationToRender.overrideForCommunicationId}
          orgId={automationToRender.orgId}
          orgName={orgName}
          onClose={toggleViewDefault}
        />
      )}
    </div>
  );
};
